import type { AccessSourceType, PermissionType } from '@thinkalpha/common/contracts/permissions.js';
import type { Route } from '@thinkalpha/ui-types/types/admin/lab';
import { getAllowSubjectIds } from 'src/admin/lib/permissions';

/** Where an inherited route entitlement comes from: a group or firm the user belongs to. */
export interface InheritedRouteAccess {
    entityId: string;
    entityName: string;
    entityType: AccessSourceType;
    permission: PermissionType;
}

/** One row of the Route Entitlements list. */
export interface RouteEntitlementRow {
    readonly id: string;
    readonly name: string;
    /** Owning firm display name. */
    readonly firmName: string;
    /** The user is a direct allow subject on the route (any of admin, owner, writer). */
    readonly entitled: boolean;
    /** Present when a group or firm of the user grants the route; access cannot be added here, only a redundant direct grant removed. */
    readonly inherited: InheritedRouteAccess | null;
    /** A permission write for this route is in flight. */
    readonly pending: boolean;
}

export interface UserRouteEntitlementsState {
    userId: string | null;
    search: string;
    /** Route ids with a single-route write in flight. */
    pendingRouteIds: readonly string[];
    /** Enable all / Clear all in flight; every non-inherited row is pending. */
    bulkPending: boolean;
}

export const initialUserRouteEntitlementsState: UserRouteEntitlementsState = {
    userId: null,
    search: '',
    pendingRouteIds: [],
    bulkPending: false,
};

/** Permission sets that entitle a user to a route; `readers` is not enough to use it. */
const ENTITLING_SETS = ['admins', 'owners', 'writers'] as const;

/** The user is a direct allow subject on one of the entitling permission sets. */
export function isDirectlyEntitled(route: Route, userId: string): boolean {
    return ENTITLING_SETS.some((set) => getAllowSubjectIds(route.permissions[set]).includes(userId));
}

/** Entitling sets with their permission name, strongest first. */
const ENTITLING_PERMISSIONS = [
    ['admins', 'admin'],
    ['owners', 'owner'],
    ['writers', 'writer'],
] as const satisfies ReadonlyArray<readonly [(typeof ENTITLING_SETS)[number], PermissionType]>;

/** Permission names of the entitling sets; revoking a route must drop all of them. */
export const ENTITLING_ACCESS_TYPES: readonly PermissionType[] = ENTITLING_PERMISSIONS.map(([, access]) => access);

export interface AccessEntity {
    id: string;
    name: string;
    type: AccessSourceType;
}

/**
 * The first group/firm of the user that is an allow subject on an entitling set, strongest
 * permission first. Reader-only access does not entitle the user, so it is not inheritance.
 */
export function findInheritedAccess(
    route: Route,
    entities: ReadonlyMap<string, AccessEntity>,
): InheritedRouteAccess | null {
    for (const [set, permission] of ENTITLING_PERMISSIONS) {
        for (const subjectId of getAllowSubjectIds(route.permissions[set])) {
            const entity = entities.get(subjectId);
            if (entity) {
                return { entityId: entity.id, entityName: entity.name, entityType: entity.type, permission };
            }
        }
    }
    return null;
}

/** Rows first by entitlement (direct or inherited), then by name. */
export function compareRows(a: RouteEntitlementRow, b: RouteEntitlementRow): number {
    const aOn = a.entitled || a.inherited !== null;
    const bOn = b.entitled || b.inherited !== null;
    if (aOn !== bOn) return aOn ? -1 : 1;
    return a.name.localeCompare(b.name);
}

export function matchesSearch(row: RouteEntitlementRow, search: string): boolean {
    const haystack = [row.id, row.name, row.firmName, row.inherited?.entityName ?? ''];
    return haystack.some((text) => text.toLowerCase().includes(search));
}
